import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useMultiTenant } from '@/hooks/useMultiTenant';

interface InternalTransferParams {
  originAccountId: string;
  destinationAccountId: string;
  amount: number;
  exitDate: string;
  description?: string;
  entryDate?: string;
  feeAmount?: number;
  feeAccountId?: string;
  bankTransactionId?: string;
}

export interface InternalTransferResult {
  debit_id: string;
  credit_id: string;
  fee_expense_id: string | null;
  origin_status: string;
  destination_status: string;
  exit_date: string;
  entry_date: string;
}

interface UseInternalTransferOptions {
  silentSuccess?: boolean;
}

export const useInternalTransfer = (options: UseInternalTransferOptions = {}) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { companyId } = useMultiTenant();
  const { silentSuccess = false } = options;

  return useMutation({
    mutationFn: async (params: InternalTransferParams): Promise<InternalTransferResult> => {
      if (!companyId) throw new Error('Company ID não disponível');

      // Params opcionais omitidos (undefined) caem no DEFAULT NULL da RPC
      const { data, error } = await supabase.rpc('create_internal_transfer_atomic', {
        p_company_id: companyId,
        p_origin_account_id: params.originAccountId,
        p_destination_account_id: params.destinationAccountId,
        p_amount: params.amount,
        p_exit_date: params.exitDate,
        p_description: params.description ?? undefined,
        p_entry_date: params.entryDate ?? undefined,
        p_fee_amount: params.feeAmount ?? undefined,
        p_fee_account_id: params.feeAccountId ?? undefined,
        p_bank_transaction_id: params.bankTransactionId ?? undefined,
      });

      if (error) throw error;
      return data as unknown as InternalTransferResult;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['account-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['bank-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['system-payments'] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['dre-data-hierarchical'] });
      queryClient.invalidateQueries({ queryKey: ['dre-indicators'] });
      queryClient.invalidateQueries({ queryKey: ['cash-flow'] });
      queryClient.invalidateQueries({ queryKey: ['account-balance-check'] });
      if (!silentSuccess) {
        toast({ title: 'Transferência registrada' });
      }
    },
    onError: (error: { message?: string }) => {
      toast({
        title: 'Erro ao registrar transferência',
        description: error?.message ?? 'Erro desconhecido',
        variant: 'destructive',
      });
    },
  });
};
